const STATIC_GA4_MEASUREMENT_ID = "G-950054X6KK";
const DEFAULT_TEST_PHASE = "live_sales";
const GA4_PRODUCTION_HOSTNAMES = new Set(["diet-compass.com", "www.diet-compass.com"]);
const INTERNAL_TRAFFIC_QUERY_PARAM = "rm_internal";
const INTERNAL_TRAFFIC_STORAGE_KEY = "reset_meal_internal_traffic";
const INTERNAL_TRAFFIC_COOKIE = "reset_meal_internal_traffic";
const ANALYTICS_SESSION_SUPPRESS_KEY = "reset_meal_analytics_suppressed";
const ANALYTICS_NOISE_REFERRER_HOSTS = new Set(["admin.shopify.com"]);
const ANALYTICS_NOISE_QUERY_PARAMS = new Set([
  "preview_key",
  "gtm_latency",
  "header_check",
  "journal_check",
]);
const ANALYTICS_NOISE_PATH_PREFIXES = ["/products_preview"];
const TRACK_PARAM_KEYS = new Set([
  "contentId",
  "productId",
  "productType",
  "ctaTarget",
  "testPhase",
  "sourcePage",
  "destinationUrl",
  "formId",
  "formType",
]);

function toSnakeCase(value) {
  return value.replace(/[A-Z]/g, (letter) => `_${letter.toLowerCase()}`);
}

function normalizeSourcePage() {
  return window.location.pathname || "/";
}

function normalizeDestinationUrl(rawUrl) {
  if (!rawUrl) return "";

  try {
    const url = new URL(rawUrl, window.location.href);
    if (url.origin === window.location.origin) {
      return `${url.pathname}${url.search}${url.hash}`;
    }
    return url.href;
  } catch {
    return rawUrl;
  }
}

function readLocalStorageValue(key) {
  try {
    return window.localStorage?.getItem(key) || "";
  } catch {
    return "";
  }
}

function writeLocalStorageValue(key, value) {
  try {
    if (value) {
      window.localStorage?.setItem(key, value);
    } else {
      window.localStorage?.removeItem(key);
    }
  } catch {
    return;
  }
}

function readSessionStorageValue(key) {
  try {
    return window.sessionStorage?.getItem(key) || "";
  } catch {
    return "";
  }
}

function writeSessionStorageValue(key, value) {
  try {
    if (value) {
      window.sessionStorage?.setItem(key, value);
    } else {
      window.sessionStorage?.removeItem(key);
    }
  } catch {
    return;
  }
}

function hasInternalTrafficCookie() {
  return document.cookie
    .split(";")
    .some((cookie) => cookie.trim() === `${INTERNAL_TRAFFIC_COOKIE}=1`);
}

function setInternalTrafficFlag(enabled) {
  writeLocalStorageValue(INTERNAL_TRAFFIC_STORAGE_KEY, enabled ? "1" : "");
  document.cookie = `${INTERNAL_TRAFFIC_COOKIE}=${enabled ? "1" : ""}; Max-Age=${enabled ? 31536000 : 0}; Path=/; SameSite=Lax`;
  window.deitCompassAnalyticsDisabled = enabled;
}

function syncInternalTrafficParam() {
  const params = new URLSearchParams(window.location.search);
  const value = params.get(INTERNAL_TRAFFIC_QUERY_PARAM);
  if (value === null) return;

  const normalized = value.trim().toLowerCase();
  if (["1", "true", "on", "yes"].includes(normalized)) {
    setInternalTrafficFlag(true);
  } else if (["0", "false", "off", "no"].includes(normalized)) {
    setInternalTrafficFlag(false);
  }

  params.delete(INTERNAL_TRAFFIC_QUERY_PARAM);
  const nextSearch = params.toString();
  const nextUrl = `${window.location.pathname}${nextSearch ? `?${nextSearch}` : ""}${window.location.hash}`;
  try {
    window.history.replaceState(window.history.state, "", nextUrl);
  } catch {
    return;
  }
}

function isInternalTraffic() {
  return window.DEIT_COMPASS_ANALYTICS_DISABLED === true
    || window.DEIT_COMPASS_INTERNAL_TRAFFIC === true
    || readLocalStorageValue(INTERNAL_TRAFFIC_STORAGE_KEY) === "1"
    || hasInternalTrafficCookie();
}

function hasAnalyticsNoiseReferrer() {
  if (!document.referrer) return false;

  try {
    const hostname = new URL(document.referrer).hostname.toLowerCase();
    return ANALYTICS_NOISE_REFERRER_HOSTS.has(hostname);
  } catch {
    return false;
  }
}

function hasAnalyticsNoiseUrl() {
  const pathname = window.location.pathname || "/";
  if (ANALYTICS_NOISE_PATH_PREFIXES.some((prefix) => pathname.startsWith(prefix))) {
    return true;
  }

  const params = new URLSearchParams(window.location.search);
  return Array.from(ANALYTICS_NOISE_QUERY_PARAMS).some((key) => params.has(key));
}

function syncAnalyticsSuppressionSession() {
  if (hasAnalyticsNoiseReferrer() || hasAnalyticsNoiseUrl()) {
    writeSessionStorageValue(ANALYTICS_SESSION_SUPPRESS_KEY, "1");
    window.deitCompassAnalyticsSuppressed = true;
  }
}

function isAnalyticsSuppressed() {
  return isInternalTraffic() || readSessionStorageValue(ANALYTICS_SESSION_SUPPRESS_KEY) === "1";
}

function getGa4MeasurementId() {
  const metaId = document.querySelector("meta[name='deit-compass:ga4-measurement-id']")?.content?.trim();
  const globalId = typeof window.DEIT_COMPASS_GA4_MEASUREMENT_ID === "string"
    ? window.DEIT_COMPASS_GA4_MEASUREMENT_ID.trim()
    : "";
  const measurementId = metaId || globalId || STATIC_GA4_MEASUREMENT_ID;
  return /^G-[A-Z0-9]+$/i.test(measurementId) ? measurementId : "";
}

function canLoadGa4Tag() {
  return GA4_PRODUCTION_HOSTNAMES.has(window.location.hostname);
}

function initGa4Tag() {
  window.dataLayer = window.dataLayer || [];
  if (isAnalyticsSuppressed()) {
    window.deitCompassAnalyticsDisabled = true;
    return;
  }

  if (!canLoadGa4Tag()) {
    window.deitCompassGa4TagDisabled = true;
    window.deitCompassGa4BlockedHostname = window.location.hostname;
    return;
  }

  const measurementId = getGa4MeasurementId();
  if (!measurementId || document.querySelector(`script[src*="${measurementId}"]`)) return;

  window.gtag = window.gtag || function gtag() {
    window.dataLayer.push(arguments);
  };

  window.gtag("js", new Date());
  window.gtag("config", measurementId);

  const script = document.createElement("script");
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(measurementId)}`;
  document.head.append(script);
}

function collectTrackParams(element) {
  const params = {};

  TRACK_PARAM_KEYS.forEach((key) => {
    const value = element.dataset[key];
    if (value) params[toSnakeCase(key)] = value;
  });

  if (element instanceof HTMLAnchorElement && !params.destination_url) {
    params.destination_url = normalizeDestinationUrl(element.getAttribute("href"));
  }

  return params;
}

function trackResetMealEvent(name, params = {}) {
  if (!name || isAnalyticsSuppressed()) return;

  if (name === "add_to_cart") window.deitCompassPfcJourney?.after("pfc_after_add_to_cart");
  if (name === "begin_checkout") window.deitCompassPfcJourney?.after("pfc_after_checkout_click");

  const payload = {
    source_page: normalizeSourcePage(),
    test_phase: DEFAULT_TEST_PHASE,
    ...params,
  };

  window.dataLayer = window.dataLayer || [];
  window.deitCompassLastEvent = { event: name, ...payload };

  if (typeof window.gtag === "function") {
    window.gtag("event", name, payload);
    return;
  }

  window.dataLayer.push({ event: name, ...payload });
}

function initTrackedClicks() {
  document.addEventListener("click", (event) => {
    const target = event.target instanceof Element
      ? event.target.closest("[data-track-event]")
      : null;
    if (!(target instanceof HTMLElement)) return;

    trackResetMealEvent(target.dataset.trackEvent, collectTrackParams(target));
  });
}

function collectProductFormParams(form, cartItem = null) {
  const params = collectTrackParams(form);
  const productContainer = form.closest("[data-product-id], .product-listing-card")
    || document.querySelector("[data-track-view='product_view']");
  const variantField = form.querySelector("select[name='id'], input[name='id']");
  const quantityField = form.querySelector("input[name='quantity']");

  if (productContainer instanceof HTMLElement) {
    const containerParams = collectTrackParams(productContainer);
    Object.assign(params, containerParams);
  }

  if (variantField instanceof HTMLInputElement || variantField instanceof HTMLSelectElement) {
    params.variant_id = variantField.value;
  }

  if (quantityField instanceof HTMLInputElement) {
    const quantity = Number.parseInt(quantityField.value, 10);
    if (Number.isFinite(quantity) && quantity > 0) params.quantity = quantity;
  }

  if (cartItem && typeof cartItem === "object") {
    if (!params.product_id && cartItem.product_id) params.product_id = String(cartItem.product_id);
    if (!params.variant_id && cartItem.variant_id) params.variant_id = String(cartItem.variant_id);
    if (!params.quantity && cartItem.quantity) params.quantity = Number(cartItem.quantity);
  }

  return params;
}

function initTrackedViews() {
  const trackedViews = Array.from(document.querySelectorAll("[data-track-view]"));
  if (!trackedViews.length) return;

  const fired = new WeakSet();
  const trackView = (element) => {
    if (fired.has(element)) return;
    fired.add(element);
    trackResetMealEvent(element.dataset.trackView, collectTrackParams(element));
  };

  if (!("IntersectionObserver" in window)) {
    trackedViews.forEach(trackView);
    return;
  }

  const observer = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (!entry.isIntersecting) return;
      trackView(entry.target);
      observer.unobserve(entry.target);
    });
  }, { threshold: 0.45 });

  trackedViews.forEach((element) => observer.observe(element));
}

function initJournalLinkTracking() {
  document.querySelectorAll("[data-journal-content-id]").forEach((article) => {
    const contentId = article.dataset.journalContentId || article.dataset.contentId || "";
    if (!contentId) return;

    const productId = article.dataset.journalProductId || article.dataset.productId || "";
    const testPhase = article.dataset.testPhase || DEFAULT_TEST_PHASE;

    article.querySelectorAll(".article-detail__content a[href]").forEach((link) => {
      if (!(link instanceof HTMLAnchorElement) || link.dataset.trackEvent) return;

      let url;
      try {
        url = new URL(link.getAttribute("href"), window.location.href);
      } catch {
        return;
      }
      if (url.origin !== window.location.origin) return;

      let eventName = "";
      let ctaTarget = "";
      if (url.pathname === "/pages/diagnosis" || url.pathname === "/pages/pfc-calculator") {
        eventName = "journal_to_diagnosis";
        ctaTarget = url.pathname === "/pages/pfc-calculator" ? "pfc_calculator" : "diagnosis";
      } else if (url.pathname === "/pages/products" || url.pathname.startsWith("/products/") || url.pathname.startsWith("/collections/")) {
        eventName = "journal_to_product";
        ctaTarget = "product";
      } else {
        return;
      }

      link.dataset.trackEvent = eventName;
      link.dataset.contentId = contentId;
      link.dataset.ctaTarget = ctaTarget;
      link.dataset.destinationUrl = normalizeDestinationUrl(link.getAttribute("href"));
      link.dataset.testPhase = testPhase;
      if (productId) link.dataset.productId = productId;
    });
  });
}

function initAnalytics() {
  syncInternalTrafficParam();
  syncAnalyticsSuppressionSession();
  initJournalLinkTracking();

  if (isAnalyticsSuppressed()) {
    window.deitCompassAnalyticsDisabled = true;
    return;
  }

  initGa4Tag();
  initTrackedClicks();
  initTrackedViews();
}

function initMobileMenu() {
  const body = document.body;
  const menuButton = document.querySelector("[data-menu-button]");
  const siteNav = document.querySelector("[data-site-nav]");
  if (!menuButton || !siteNav) return;

  let lastFocus = null;

  const closeMenu = () => {
    menuButton.setAttribute("aria-expanded", "false");
    siteNav.classList.remove("is-open");
    body.classList.remove("menu-open");
    if (lastFocus instanceof HTMLElement) lastFocus.focus();
  };

  const openMenu = () => {
    lastFocus = document.activeElement;
    menuButton.setAttribute("aria-expanded", "true");
    siteNav.classList.add("is-open");
    body.classList.add("menu-open");
    const firstLink = siteNav.querySelector("a, button");
    if (firstLink instanceof HTMLElement) firstLink.focus();
  };

  menuButton.addEventListener("click", () => {
    const isOpen = menuButton.getAttribute("aria-expanded") === "true";
    if (isOpen) {
      closeMenu();
    } else {
      openMenu();
    }
  });

  siteNav.addEventListener("click", (event) => {
    if (event.target instanceof HTMLAnchorElement) closeMenu();
  });

  document.addEventListener("keydown", (event) => {
    if (event.key === "Escape" && menuButton.getAttribute("aria-expanded") === "true") {
      closeMenu();
    }
  });
}

function initDropdowns() {
  document.querySelectorAll("[data-dropdown]").forEach((dropdown) => {
    const button = dropdown.querySelector("[data-dropdown-button]");
    if (!(button instanceof HTMLButtonElement)) return;

    const closeDropdown = () => {
      button.setAttribute("aria-expanded", "false");
      dropdown.classList.remove("is-open");
    };

    button.addEventListener("click", () => {
      const isOpen = button.getAttribute("aria-expanded") === "true";
      button.setAttribute("aria-expanded", String(!isOpen));
      dropdown.classList.toggle("is-open", !isOpen);
    });

    dropdown.addEventListener("keydown", (event) => {
      if (event.key === "Escape") {
        closeDropdown();
        button.focus();
      }
    });

    document.addEventListener("click", (event) => {
      if (!dropdown.contains(event.target)) closeDropdown();
    });
  });
}

function initQuiz() {
  const quizForm = document.querySelector("[data-quiz-form]");
  if (!quizForm) return;

  const steps = Array.from(quizForm.querySelectorAll("[data-step]"));
  const backButton = quizForm.querySelector("[data-quiz-back]");
  const nextButton = quizForm.querySelector("[data-quiz-next]");
  const restartButton = quizForm.querySelector("[data-quiz-restart]");
  const resultPanel = quizForm.querySelector("[data-quiz-result]");
  const error = quizForm.querySelector("[data-quiz-error]");
  const stepLabel = quizForm.querySelector("[data-quiz-step-label]");
  const progress = quizForm.querySelector("[data-quiz-progress]");
  const resultLabel = quizForm.querySelector("[data-result-label]");
  const resultTitle = quizForm.querySelector("[data-result-title]");
  const resultCopy = quizForm.querySelector("[data-result-copy]");
  const resultProductLink = quizForm.querySelector("[data-result-product-link]");
  let currentStep = 0;
  let hasTrackedDiagnosisStart = false;

  const trackDiagnosisStart = () => {
    if (hasTrackedDiagnosisStart) return;
    hasTrackedDiagnosisStart = true;
    trackResetMealEvent("diagnosis_start");
  };

  const showStep = (index) => {
    currentStep = index;
    steps.forEach((step, stepIndex) => {
      step.classList.toggle("is-active", stepIndex === currentStep);
    });
    if (stepLabel) stepLabel.textContent = `${currentStep + 1} / ${steps.length}`;
    if (progress) progress.style.width = `${((currentStep + 1) / steps.length) * 100}%`;
    if (backButton) backButton.disabled = currentStep === 0;
    if (nextButton) {
      nextButton.hidden = false;
      nextButton.textContent = currentStep === steps.length - 1 ? "結果を見る" : "次へ";
    }
    if (error) error.hidden = true;
    if (resultPanel) resultPanel.hidden = true;
  };

  const selectedInput = () => steps[currentStep]?.querySelector("input[type='radio']:checked");

  const buildResult = () => {
    const values = Array.from(quizForm.querySelectorAll("input[type='radio']:checked")).map((input) => input.value);
    const warmScore = values.filter((value) => value === "warm").length;
    const portableScore = values.filter((value) => value === "portable").length;
    const isWarm = warmScore >= portableScore;
    const productType = isWarm ? "warm" : "portable";

    if (resultLabel) resultLabel.textContent = isWarm ? "RESET WARM タイプ" : "RESET PORTABLE タイプ";
    if (resultTitle) {
      resultTitle.textContent = isWarm
        ? "温かい一品のほうが取り入れやすい可能性があります。"
        : "持ち歩ける個包装のほうが取り入れやすい可能性があります。";
    }
    if (resultCopy) {
      resultCopy.textContent = isWarm
        ? "甘い食品を毎日続けるより、食生活が崩れた日に温かい一品を使う流れが合いそうです。"
        : "会社、出張、移動中など準備しにくい場面で、迷わず取り出せる形が合いそうです。";
    }
    if (resultProductLink instanceof HTMLAnchorElement) {
      resultProductLink.href = isWarm ? "/pages/products#warm" : "/pages/products#portable";
      resultProductLink.textContent = isWarm ? "RESET WARMを見る" : "RESET PORTABLEを見る";
      resultProductLink.dataset.productId = isWarm ? "GV-P001" : "GV-SD001";
      resultProductLink.dataset.productType = productType;
    }

    return productType;
  };

  quizForm.addEventListener("change", (event) => {
    if (event.target instanceof HTMLInputElement && event.target.type === "radio") {
      trackDiagnosisStart();
    }
  });

  nextButton?.addEventListener("click", () => {
    if (!selectedInput()) {
      if (error) error.hidden = false;
      return;
    }

    if (currentStep < steps.length - 1) {
      showStep(currentStep + 1);
      return;
    }

    steps.forEach((step) => step.classList.remove("is-active"));
    const productType = buildResult();
    trackResetMealEvent("diagnosis_complete", { product_type: productType });
    if (resultPanel) resultPanel.hidden = false;
    if (stepLabel) stepLabel.textContent = "結果";
    if (progress) progress.style.width = "100%";
    if (backButton) backButton.disabled = true;
    nextButton.hidden = true;
    resultPanel?.querySelector("a, button")?.focus();
  });

  backButton?.addEventListener("click", () => {
    if (currentStep > 0) showStep(currentStep - 1);
  });

  restartButton?.addEventListener("click", () => {
    quizForm.reset();
    hasTrackedDiagnosisStart = false;
    showStep(0);
    steps[0]?.querySelector("input")?.focus();
  });

  showStep(0);
}

function initStaticForms() {
  document.querySelectorAll("[data-static-form]").forEach((form) => {
    form.addEventListener("submit", (event) => {
      event.preventDefault();
      form.classList.add("was-validated");
      if (!form.checkValidity()) {
        form.reportValidity();
        return;
      }

      const submitEventName = form.getAttribute("data-track-submit-event");
      if (submitEventName) {
        trackResetMealEvent(submitEventName, collectTrackParams(form));
      }

      const feedback = form.querySelector("[data-form-feedback]");
      if (feedback) {
        feedback.hidden = false;
        feedback.scrollIntoView({ block: "nearest" });
      }

      if (form.hasAttribute("data-mail-form")) {
        const to = form.getAttribute("data-mail-to");
        if (!to) return;
        const subject = form.getAttribute("data-mail-subject") || document.title;
        const fields = Array.from(form.querySelectorAll("input, select, textarea"));
        const lines = fields.flatMap((field) => {
          if (!field.name || field.disabled) return [];
          if ((field.type === "radio" || field.type === "checkbox") && !field.checked) return [];
          const fieldset = field.closest("fieldset");
          const label = fieldset?.querySelector("legend")?.textContent?.trim()
            || (field.id ? form.querySelector(`label[for="${field.id}"]`)?.textContent?.trim() : "")
            || field.closest("label")?.textContent?.trim()
            || field.name;
          const rawValue = field.type === "checkbox"
            ? "同意済み"
            : field.closest("label")?.textContent?.replace(label, "").trim() || field.value.trim();
          const value = rawValue || field.value.trim();
          return value ? [`${label}: ${value}`] : [];
        });
        const body = [
          "以下の内容で問い合わせます。",
          "",
          ...lines,
          "",
          `送信ページ: ${window.location.href}`
        ].join("\n");

        window.location.href = `mailto:${to}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
      }
    });
  });
}

function initNativeTrackedForms() {
  document.querySelectorAll("form[data-track-submit-event]:not([data-static-form])").forEach((form) => {
    form.addEventListener("submit", () => {
      if (form instanceof HTMLFormElement && !form.checkValidity()) return;

      const submitEventName = form.getAttribute("data-track-submit-event");
      if (submitEventName) {
        trackResetMealEvent(submitEventName, collectTrackParams(form));
      }
    });
  });
}

function initCheckoutTracking() {
  document.addEventListener("click", (event) => {
    const target = event.target instanceof Element
      ? event.target.closest("[data-cart-checkout], button[name='checkout'], input[name='checkout']")
      : null;
    if (!(target instanceof HTMLElement)) return;
    if (target instanceof HTMLButtonElement && target.disabled) return;
    if (target instanceof HTMLInputElement && target.disabled) return;

    const params = collectTrackParams(target);
    trackResetMealEvent("begin_checkout", params);
    trackResetMealEvent("own_store_checkout_click", params);
  });
}

function initMetrics() {
  const resetMealMetrics = {
    responses: "集計中",
    warmRate: "集計中",
    portableRate: "集計中",
    paidIntent: "集計中",
    barriers: "集計中",
    frequency: "集計中",
  };

  document.querySelectorAll("[data-metric]").forEach((node) => {
    const key = node.getAttribute("data-metric");
    if (key && resetMealMetrics[key]) node.textContent = resetMealMetrics[key];
  });
}

function initSupportExperience() {
  document.querySelectorAll("[data-reset-support-experience]").forEach((experience) => {
    const tabs = Array.from(experience.querySelectorAll("[data-reset-support-tab]"));
    const panels = Array.from(experience.querySelectorAll("[data-reset-support-panel]"));
    if (!tabs.length || !panels.length) return;

    const activateTab = (nextTab, shouldFocus = false) => {
      const panelId = nextTab.getAttribute("aria-controls");
      tabs.forEach((tab) => {
        const isActive = tab === nextTab;
        tab.classList.toggle("is-active", isActive);
        tab.setAttribute("aria-selected", String(isActive));
        tab.setAttribute("tabindex", isActive ? "0" : "-1");
      });

      panels.forEach((panel) => {
        const isActive = panel.id === panelId;
        panel.hidden = !isActive;
        panel.classList.toggle("is-active", isActive);
      });

      if (shouldFocus) nextTab.focus();
    };

    tabs.forEach((tab, index) => {
      tab.addEventListener("click", () => activateTab(tab));

      tab.addEventListener("keydown", (event) => {
        let nextIndex = null;
        if (event.key === "ArrowRight") nextIndex = (index + 1) % tabs.length;
        if (event.key === "ArrowLeft") nextIndex = (index - 1 + tabs.length) % tabs.length;
        if (event.key === "Home") nextIndex = 0;
        if (event.key === "End") nextIndex = tabs.length - 1;
        if (nextIndex === null) return;

        event.preventDefault();
        activateTab(tabs[nextIndex], true);
      });
    });

    const selectedTab = tabs.find((tab) => tab.getAttribute("aria-selected") === "true") || tabs[0];
    activateTab(selectedTab);
  });
}

function initPopularProductsSlider() {
  document.querySelectorAll("[data-popular-products-slider]").forEach((slider) => {
    const track = slider.querySelector("[data-popular-products-track]");
    const slides = Array.from(slider.querySelectorAll("[data-popular-products-slide]"));
    if (!track || slides.length < 2) return;

    const section = slider.closest(".popular-products");
    const prevButton = section?.querySelector("[data-popular-products-prev]");
    const nextButton = section?.querySelector("[data-popular-products-next]");
    const dots = Array.from(section?.querySelectorAll("[data-popular-products-dot]") || []);
    const focusableSelector = "a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]";

    let currentIndex = slides.findIndex((slide) => slide.classList.contains("is-active"));
    if (currentIndex < 0) currentIndex = 0;

    let timer = 0;
    const prefersReducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    const setSlideFocusability = (slide, isActive) => {
      slide.querySelectorAll(focusableSelector).forEach((element) => {
        if (!(element instanceof HTMLElement)) return;
        if (!isActive) {
          if (!element.dataset.popularProductsOriginalTabindex && element.hasAttribute("tabindex")) {
            element.dataset.popularProductsOriginalTabindex = element.getAttribute("tabindex") || "";
          }
          element.setAttribute("tabindex", "-1");
          return;
        }

        if (element.dataset.popularProductsOriginalTabindex !== undefined) {
          const original = element.dataset.popularProductsOriginalTabindex;
          if (original) {
            element.setAttribute("tabindex", original);
          } else {
            element.removeAttribute("tabindex");
          }
          delete element.dataset.popularProductsOriginalTabindex;
        } else if (element.getAttribute("tabindex") === "-1") {
          element.removeAttribute("tabindex");
        }
      });
    };

    const updateStatus = () => {
      track.style.transform = `translateX(-${currentIndex * 100}%)`;
      slides.forEach((slide, index) => {
        const isActive = index === currentIndex;
        slide.classList.toggle("is-active", isActive);
        slide.setAttribute("aria-hidden", String(!isActive));
        setSlideFocusability(slide, isActive);
      });
      dots.forEach((dot, index) => {
        const isActive = index === currentIndex;
        dot.classList.toggle("is-active", isActive);
        dot.setAttribute("aria-current", String(isActive));
      });
    };

    const goToSlide = (nextIndex) => {
      const normalizedNextIndex = (nextIndex + slides.length) % slides.length;
      if (normalizedNextIndex === currentIndex) return;
      currentIndex = normalizedNextIndex;
      updateStatus();
    };

    const showNextSlide = () => goToSlide(currentIndex + 1);
    const showPreviousSlide = () => goToSlide(currentIndex - 1);

    const startTimer = () => {
      if (prefersReducedMotion || timer) return;
      timer = window.setInterval(showNextSlide, 3000);
    };

    const stopTimer = () => {
      window.clearInterval(timer);
      timer = 0;
    };

    prevButton?.addEventListener("click", () => {
      stopTimer();
      showPreviousSlide();
      startTimer();
    });

    nextButton?.addEventListener("click", () => {
      stopTimer();
      showNextSlide();
      startTimer();
    });

    dots.forEach((dot) => {
      dot.addEventListener("click", () => {
        const targetIndex = Number.parseInt(dot.dataset.popularProductsTarget || "0", 10);
        if (!Number.isFinite(targetIndex)) return;
        stopTimer();
        goToSlide(targetIndex);
        startTimer();
      });
    });

    slider.addEventListener("mouseenter", stopTimer);
    slider.addEventListener("mouseleave", startTimer);
    slider.addEventListener("focusin", stopTimer);
    slider.addEventListener("focusout", startTimer);

    updateStatus();
    startTimer();
  });
}

function initMobileCta() {
  const mobileCta = document.querySelector("[data-mobile-cta]");
  const closeMobileCta = document.querySelector("[data-close-mobile-cta]");
  if (!mobileCta) return;

  closeMobileCta?.addEventListener("click", () => {
    mobileCta.classList.add("is-hidden");
  });

  const hideTargets = Array.from(document.querySelectorAll("form, footer, .quiz-section, .contact-section, .support-visual-hero, .support-story, .support-food-mosaic, .support-final, .popular-products, .product-strip"));
  const syncMobileCta = () => {
    const shouldCheck = window.matchMedia("(max-width: 860px)").matches;
    const hasVisibleTarget = shouldCheck && hideTargets.some((target) => {
      const rect = target.getBoundingClientRect();
      return rect.bottom > 0 && rect.top < window.innerHeight;
    });
    mobileCta.classList.toggle("is-auto-hidden", hasVisibleTarget);
  };

  syncMobileCta();
  requestAnimationFrame(syncMobileCta);
  window.setTimeout(syncMobileCta, 150);
  window.addEventListener("scroll", syncMobileCta, { passive: true });
  window.addEventListener("resize", syncMobileCta);
  window.addEventListener("hashchange", syncMobileCta);
}

function getShopifyRoute(path) {
  const root = window.Shopify?.routes?.root || "/";
  return `${root.replace(/\/?$/, "/")}${path.replace(/^\//, "")}`;
}

function initFixedUiOffset() {
  const syncOffset = () => {
    const viewportHeight = window.innerHeight;
    const viewportWidth = window.innerWidth;
    const offset = Array.from(document.querySelectorAll("iframe")).reduce((maxOffset, frame) => {
      const rect = frame.getBoundingClientRect();
      const style = window.getComputedStyle(frame);
      if (frame.src.includes("shopifycloud/preview-bar")) {
        return Math.max(maxOffset, 80);
      }
      const touchesBottom = rect.bottom >= viewportHeight - 4;
      const looksLikeToolbar = style.position === "fixed" && rect.width >= viewportWidth * 0.5 && rect.height >= 36 && rect.height <= 140;
      return touchesBottom && looksLikeToolbar ? Math.max(maxOffset, rect.height) : maxOffset;
    }, 0);

    document.documentElement.style.setProperty("--reset-fixed-ui-bottom", `${Math.round(offset)}px`);
  };

  syncOffset();
  window.setTimeout(syncOffset, 500);
  window.setTimeout(syncOffset, 1600);
  window.addEventListener("resize", syncOffset);
  new MutationObserver(syncOffset).observe(document.body, { childList: true, subtree: true, attributes: true });
}

function escapeHtml(value) {
  return String(value ?? "").replace(/[&<>"']/g, (character) => ({
    "&": "&amp;",
    "<": "&lt;",
    ">": "&gt;",
    "\"": "&quot;",
    "'": "&#39;",
  }[character]));
}

function formatCartMoney(value, currency = "JPY") {
  const amount = Number(value);
  if (!Number.isFinite(amount)) return "";

  if (window.Shopify && typeof window.Shopify.formatMoney === "function") {
    return window.Shopify.formatMoney(amount);
  }

  return new Intl.NumberFormat("ja-JP", {
    style: "currency",
    currency,
    maximumFractionDigits: currency === "JPY" ? 0 : 2,
  }).format(amount / 100);
}

function getCartTaxLabel(cart) {
  if (typeof cart?.taxes_included !== "boolean") {
    return document.querySelector("[data-cart-tax-label]")?.dataset.cartTaxLabel || "税抜";
  }
  return cart?.taxes_included ? "税込" : "税抜";
}

function formatCartMoneyWithTax(value, currency = "JPY", taxLabel = "税抜") {
  return `<span>${escapeHtml(formatCartMoney(value, currency))}</span><span class="price-tax-label">(${escapeHtml(taxLabel)})</span>`;
}

function renderCartDrawerItems(cart) {
  const itemsContainer = document.querySelector("[data-cart-items]");
  const countNodes = document.querySelectorAll("[data-cart-count]");
  const totalNode = document.querySelector("[data-cart-total]");
  const checkoutButton = document.querySelector("[data-cart-checkout]");
  const itemCount = Number(cart?.item_count || 0);
  const taxLabel = getCartTaxLabel(cart);

  countNodes.forEach((node) => {
    node.textContent = itemCount > 99 ? "99+" : String(itemCount);
  });

  if (totalNode) {
    totalNode.innerHTML = formatCartMoneyWithTax(cart?.total_price || 0, cart?.currency || "JPY", taxLabel);
  }

  if (checkoutButton instanceof HTMLButtonElement) {
    checkoutButton.disabled = itemCount === 0;
  }

  if (!itemsContainer) return;

  if (!itemCount) {
    itemsContainer.innerHTML = `
      <div class="cart-drawer__empty">
        <p>カートは空です。</p>
        <a class="button button-accent" href="/pages/products">商品を見る</a>
      </div>
    `;
    return;
  }

  const items = Array.isArray(cart.items) ? cart.items : [];
  itemsContainer.innerHTML = `
    <ul class="cart-drawer__items">
      ${items.map((item) => {
        const title = item.product_title || item.title || "商品";
        const variantTitle = item.variant_title && item.variant_title !== "Default Title" ? item.variant_title : "";
        const image = item.image
          ? `<img src="${escapeHtml(item.image)}" alt="${escapeHtml(title)}" loading="lazy">`
          : "<span aria-hidden=\"true\"></span>";
        return `
          <li class="cart-drawer__item">
            <a class="cart-drawer__item-media" href="${escapeHtml(item.url || "/cart")}">${image}</a>
            <div class="cart-drawer__item-copy">
              <a href="${escapeHtml(item.url || "/cart")}">${escapeHtml(title)}</a>
              ${variantTitle ? `<span>${escapeHtml(variantTitle)}</span>` : ""}
              <span>数量 ${escapeHtml(item.quantity || 0)}</span>
            </div>
            <strong>${formatCartMoneyWithTax(item.final_line_price ?? item.line_price ?? 0, cart.currency || "JPY", taxLabel)}</strong>
          </li>
        `;
      }).join("")}
    </ul>
  `;
}

async function fetchCartState() {
  const response = await fetch(getShopifyRoute("cart.js"), {
    headers: { Accept: "application/json" },
  });

  if (!response.ok) {
    throw new Error("カート情報を取得できませんでした。");
  }

  return response.json();
}

function initCartDrawer() {
  const drawer = document.querySelector("[data-cart-drawer]");
  const toggle = document.querySelector("[data-cart-toggle]");
  const backdrop = document.querySelector("[data-cart-backdrop]");
  if (!drawer || !(toggle instanceof HTMLButtonElement) || !backdrop) return;

  let lastFocus = null;

  const refreshCart = async () => {
    const cart = await fetchCartState();
    renderCartDrawerItems(cart);
    return cart;
  };

  const openDrawer = async (shouldRefresh = true) => {
    if (shouldRefresh) {
      try {
        await refreshCart();
      } catch {
      }
    }

    lastFocus = document.activeElement;
    backdrop.hidden = false;
    document.body.classList.add("cart-drawer-open");
    toggle.setAttribute("aria-expanded", "true");
    drawer.setAttribute("aria-hidden", "false");
    requestAnimationFrame(() => {
      backdrop.classList.add("is-open");
      drawer.classList.add("is-open");
      if (drawer instanceof HTMLElement) drawer.focus();
    });
  };

  const closeDrawer = () => {
    document.body.classList.remove("cart-drawer-open");
    toggle.setAttribute("aria-expanded", "false");
    drawer.setAttribute("aria-hidden", "true");
    backdrop.classList.remove("is-open");
    drawer.classList.remove("is-open");
    window.setTimeout(() => {
      if (!drawer.classList.contains("is-open")) backdrop.hidden = true;
    }, 210);
    if (lastFocus instanceof HTMLElement) lastFocus.focus();
  };

  window.openResetCartDrawer = openDrawer;
  window.refreshResetCartDrawer = refreshCart;

  toggle.addEventListener("click", () => openDrawer(true));
  document.querySelectorAll("[data-cart-close]").forEach((button) => {
    button.addEventListener("click", closeDrawer);
  });

  drawer.addEventListener("keydown", (event) => {
    if (event.key === "Escape") {
      closeDrawer();
      return;
    }

    if (event.key !== "Tab") return;

    const focusable = Array.from(drawer.querySelectorAll("a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex='-1'])"))
      .filter((element) => element instanceof HTMLElement && !element.hidden && element.offsetParent !== null);
    if (!focusable.length) return;

    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  });

  refreshCart().catch(() => {});
}

function setProductFormFeedback(form, message, isError = false) {
  const feedback = form.querySelector("[data-product-form-feedback]");
  if (!feedback) return;
  feedback.textContent = message;
  feedback.classList.toggle("is-error", isError);
}

function normalizeCartAddError(error) {
  const message = String(error?.description || error?.message || "").trim();
  if (/cannot find variant/i.test(message)) {
    return "このプレビュー商品はカートに追加できません。公開後に追加を確認できます。";
  }
  if (/sold out|unavailable|not available/i.test(message)) {
    return "この商品は現在カートに追加できません。";
  }
  return message || "カートに追加できませんでした。";
}

function initProductCartForms() {
  document.querySelectorAll(".product-purchase-form, .product-sticky-purchase__form, .popular-products__form").forEach((form) => {
    form.addEventListener("submit", async (event) => {
      const submitter = event.submitter instanceof HTMLButtonElement
        ? event.submitter
        : form.querySelector("button[name='add']");
      if (submitter?.name && submitter.name !== "add") return;
      if (!(form instanceof HTMLFormElement) || !(submitter instanceof HTMLButtonElement)) return;

      event.preventDefault();
      const originalText = submitter.textContent;
      const wasDisabled = submitter.disabled;
      submitter.disabled = true;
      submitter.textContent = "追加中";
      setProductFormFeedback(form, "");

      try {
        const response = await fetch(getShopifyRoute("cart/add.js"), {
          method: "POST",
          headers: { Accept: "application/json" },
          body: new FormData(form),
        });

        if (!response.ok) {
          const error = await response.json().catch(() => null);
          throw new Error(normalizeCartAddError(error));
        }

        const cartItem = await response.json().catch(() => null);
        trackResetMealEvent("add_to_cart", collectProductFormParams(form, cartItem));

        const cart = typeof window.refreshResetCartDrawer === "function"
          ? await window.refreshResetCartDrawer()
          : await fetchCartState();
        renderCartDrawerItems(cart);
        setProductFormFeedback(form, "カートに追加しました。");
        submitter.textContent = "追加済み";
        if (typeof window.openResetCartDrawer === "function") {
          window.openResetCartDrawer(false);
        }
        window.setTimeout(() => {
          submitter.textContent = originalText;
        }, 1400);
      } catch (error) {
        submitter.textContent = originalText;
        setProductFormFeedback(form, error.message || "カートに追加できませんでした。", true);
      } finally {
        window.setTimeout(() => {
          submitter.disabled = wasDisabled;
        }, 300);
      }
    });
  });
}

function initProductStickyPurchase() {
  const stickyBar = document.querySelector("[data-product-sticky-purchase]");
  const mainForm = document.querySelector(".product-purchase-form");
  const mainSubmit = mainForm?.querySelector(".product-submit");
  if (!stickyBar || !mainForm || !(mainSubmit instanceof HTMLElement)) return;

  const mainQuantity = mainForm.querySelector("input[name='quantity']");
  const mainVariant = mainForm.querySelector("select[name='id'], input[name='id']");
  const stickyQuantity = stickyBar.querySelector("[data-sticky-quantity]");
  const stickyVariant = stickyBar.querySelector("[data-sticky-variant-id]");
  const stickyPrice = stickyBar.querySelector("[data-sticky-product-price]");

  const syncFromMain = () => {
    if (stickyQuantity instanceof HTMLInputElement && mainQuantity instanceof HTMLInputElement) {
      stickyQuantity.value = mainQuantity.value || "1";
    }

    if (stickyVariant instanceof HTMLInputElement && mainVariant instanceof HTMLInputElement) {
      stickyVariant.value = mainVariant.value;
    }

    if (stickyVariant instanceof HTMLInputElement && mainVariant instanceof HTMLSelectElement) {
      const selectedOption = mainVariant.selectedOptions[0];
      stickyVariant.value = selectedOption?.value || mainVariant.value;
      if (stickyPrice && selectedOption?.dataset.price) {
        stickyPrice.textContent = selectedOption.dataset.price;
      }
    }
  };

  const syncQuantityToMain = () => {
    if (stickyQuantity instanceof HTMLInputElement && mainQuantity instanceof HTMLInputElement) {
      mainQuantity.value = stickyQuantity.value || "1";
    }
  };

  const setVisible = (isVisible) => {
    stickyBar.classList.toggle("is-visible", isVisible);
    stickyBar.setAttribute("aria-hidden", String(!isVisible));
    document.body.classList.toggle("product-sticky-cart-visible", isVisible);
  };

  const syncVisibility = () => {
    const rect = mainSubmit.getBoundingClientRect();
    setVisible(rect.bottom <= 0 || rect.top >= window.innerHeight);
  };

  mainQuantity?.addEventListener("input", syncFromMain);
  mainVariant?.addEventListener("change", syncFromMain);
  stickyQuantity?.addEventListener("input", syncQuantityToMain);

  syncFromMain();

  if ("IntersectionObserver" in window) {
    const observer = new IntersectionObserver((entries) => {
      const entry = entries[0];
      setVisible(!entry?.isIntersecting);
    }, { threshold: 0.01 });
    observer.observe(mainSubmit);
  } else {
    syncVisibility();
    window.addEventListener("scroll", syncVisibility, { passive: true });
    window.addEventListener("resize", syncVisibility);
  }
}

function initQuantitySteppers() {
  document.addEventListener("click", (event) => {
    const target = event.target instanceof Element ? event.target : null;
    const stockAlertTrigger = target?.closest("[data-stock-alert-trigger]");
    if (stockAlertTrigger instanceof HTMLElement) {
      event.preventDefault();

      const stockAlertId = stockAlertTrigger.dataset.stockAlertTrigger;
      const stockAlert = stockAlertId ? document.getElementById(stockAlertId) : null;
      if (!(stockAlert instanceof HTMLElement)) return;

      window.clearTimeout(Number.parseInt(stockAlert.dataset.stockAlertTimer || "0", 10));
      stockAlert.hidden = false;
      stockAlert.classList.add("is-visible");

      const timer = window.setTimeout(() => {
        stockAlert.classList.remove("is-visible");

        window.setTimeout(() => {
          if (!stockAlert.classList.contains("is-visible")) {
            stockAlert.hidden = true;
          }
        }, 180);
      }, 3000);

      stockAlert.dataset.stockAlertTimer = String(timer);
      return;
    }

    const control = target?.closest("[data-quantity-step]");
    if (!(control instanceof HTMLElement)) return;

    const stepper = control.closest("[data-quantity-stepper]");
    const input = stepper?.querySelector("input[type='number']");
    if (!(input instanceof HTMLInputElement)) return;

    const step = Number.parseInt(control.dataset.quantityStep || "0", 10);
    if (!Number.isFinite(step) || step === 0) return;

    const current = Number.parseInt(input.value || "0", 10);
    const min = input.min === "" ? 0 : Number.parseInt(input.min, 10);
    const max = input.max === "" ? Number.POSITIVE_INFINITY : Number.parseInt(input.max, 10);
    const lowerBound = Number.isFinite(min) ? min : 0;
    const upperBound = Number.isFinite(max) ? max : Number.POSITIVE_INFINITY;
    const baseValue = Number.isFinite(current) ? current : lowerBound;
    const nextValue = Math.min(Math.max(baseValue + step, lowerBound), upperBound);

    input.value = String(nextValue);
    input.dispatchEvent(new Event("input", { bubbles: true }));
    input.dispatchEvent(new Event("change", { bubbles: true }));

    const cartLine = control.dataset.cartLine;
    if (cartLine) {
      event.preventDefault();
      stepper?.setAttribute("aria-busy", "true");
      const changeUrl = new URL(getShopifyRoute("cart/change"), window.location.origin);
      changeUrl.searchParams.set("line", cartLine);
      changeUrl.searchParams.set("quantity", String(nextValue));
      window.location.assign(`${changeUrl.pathname}${changeUrl.search}`);
    }
  });
}

initAnalytics();
initMobileMenu();
initDropdowns();
initQuiz();
initStaticForms();
initNativeTrackedForms();
initCheckoutTracking();
initMetrics();
initSupportExperience();
initPopularProductsSlider();
initMobileCta();
initFixedUiOffset();
initCartDrawer();
initProductStickyPurchase();
initProductCartForms();
initQuantitySteppers();

function createPfcJourneyTracker(deps) {
  const ttl = 30 * 60 * 1000;
  const allowedEvents = new Set(['pfc_after_page_view', 'pfc_after_product_view', 'pfc_after_support_view', 'pfc_after_cart_view', 'pfc_after_line_click', 'pfc_after_copy', 'pfc_after_add_to_cart', 'pfc_after_checkout_click']);
  let memory = null;
  function clear() { memory = null; deps.remove(); }
  function state() {
    if (!deps.allowed()) { clear(); return null; }
    let stored;
    try { stored = JSON.parse(deps.read() || 'null'); } catch { stored = null; }
    const value = stored || memory;
    if (!value || !Number.isFinite(value.startedAt) || value.startedAt <= 0 || (value.completedAt !== null && !Number.isFinite(value.completedAt))) return null;
    const age = deps.now() - (value.completedAt || value.startedAt);
    if (age < 0 || age >= ttl) { clear(); return null; }
    return {startedAt:value.startedAt, completedAt:value.completedAt};
  }
  function save(value) { memory = value; deps.write(JSON.stringify(value)); }
  function start() {
    if (!deps.allowed()) { clear(); return false; }
    if (state()) return true;
    if (!deps.send('pfc_calculation_start')) return false;
    save({startedAt:deps.now(), completedAt:null});
    return true;
  }
  function complete() {
    if (!start()) return false;
    const value = state();
    if (!value || value.completedAt !== null) return false;
    if (!deps.send('pfc_calculation_complete')) return false;
    save({...value, completedAt:deps.now()});
    return true;
  }
  function after(name) {
    if (!allowedEvents.has(name) || !state()?.completedAt) return false;
    return deps.send(name);
  }
  return {start, complete, after, clear};
}

function initPfcJourneyTracking() {
  if (!canLoadGa4Tag() || isAnalyticsSuppressed() || window.Shopify?.designMode || new URLSearchParams(location.search).has('preview_theme_id') || new URLSearchParams(location.search).has('view')) return;
  const key = 'dc_pfc_journey_v1';
  const storage = {
    read: () => { try { return sessionStorage.getItem(key); } catch { return null; } },
    write: value => { try { sessionStorage.setItem(key, value); } catch {} },
    remove: () => { try { sessionStorage.removeItem(key); } catch {} },
  };
  const permitted = () => {
    try { return !isAnalyticsSuppressed() && window.Shopify?.customerPrivacy?.analyticsProcessingAllowed() === true; } catch { return false; }
  };
  const tracker = createPfcJourneyTracker({
    now: Date.now, ...storage, allowed: permitted,
    send: name => {
      if (!permitted() || typeof window.gtag !== 'function') return false;
      let referrer = '';
      try { const url = new URL(document.referrer); referrer = url.origin + url.pathname; } catch {}
      trackResetMealEvent(name, {measurement_version:'pfc_v1', page_location:location.origin + location.pathname, page_referrer:referrer, transport_type:'beacon'});
      return true;
    },
  });
  window.deitCompassPfcJourney = tracker;
  document.addEventListener('dc:pfc-input', () => tracker.start());
  document.addEventListener('dc:pfc-result', () => tracker.complete());
  document.addEventListener('dc:pfc-copy', () => tracker.after('pfc_after_copy'));
  document.addEventListener('click', event => {
    if (!event.isTrusted) return;
    const anchor = event.target instanceof Element ? event.target.closest('a[href]') : null;
    if (!anchor) return;
    try {
      const url = new URL(anchor.getAttribute('href'), location.href);
      if (['lin.ee', 'line.me', 'liff.line.me'].includes(url.hostname)) tracker.after('pfc_after_line_click');
    } catch {}
  });
  let pageRecorded = false;
  function recordPage() {
    if (pageRecorded || !permitted()) return;
    pageRecorded = true;
    tracker.after('pfc_after_page_view');
    const path = location.pathname.replace(/^\/[a-z]{2}(?:-[A-Z]{2})?(?=\/)/, '');
    if (/^\/products\//.test(path) || /^\/collections\/[^/]+\/products\//.test(path)) tracker.after('pfc_after_product_view');
    if (path === '/pages/support') tracker.after('pfc_after_support_view');
    if (path === '/cart') tracker.after('pfc_after_cart_view');
  }
  document.addEventListener('visitorConsentCollected', () => { if (!permitted()) tracker.clear(); else recordPage(); });
  if (window.Shopify?.customerPrivacy) recordPage();
  else if (typeof window.Shopify?.loadFeatures === 'function') {
    window.Shopify.loadFeatures([{name:'consent-tracking-api', version:'0.1'}], error => { if (!error) recordPage(); });
  }
}

initPfcJourneyTracking();
